import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Exact, isCountKind, parseDecimal, PRICE, QUANTITY, STOCK_KEYS,
  type StockDocDto, type StockDocInput, type StockDocListDto, type StockDocReverseInput, type StockDocsDto, type StockKind, type StockOnHandDto, type StockOptionsDto, type StockRowDto
} from "@delta/shared";
import { useLang, type T } from "../../i18n";
import { useSession } from "../../lib/session";
import { api, ApiFailure } from "../../lib/api";
import { errorText, fieldErrorsOf } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, type Column } from "../../components/ListView";
import { Confirm, Modal, Field } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { LockedButton, lockTag } from "../settings/common";
import { Card } from "../accounting/common";
import { amt, instant, nowRiyadh, when } from "../ledger/common";
import { dec, qty as fmtQty } from "../items/common";
import { NotBuilt } from "../Simple";

// Warehouses › stock documents and stock on hand, ported from the prototype (erp-warehouses.js): Beginning Inventory,
// Corrupted Invoices (damaged stock), Warehouses Transfers, Warehouses Inventory (counts and compromises), the
// document editor, the document page and "/warehouse-stock".
//
// Rules S-1 to S-7 are provisional, for synthetic staging and accountant testing only (owner's standing instruction
// of 2026-10-26); the server refuses to post under them in production. As journal entries (L-9): a draft moves no
// stock and is in no balance; posting moves the stock at the weighted average (S-3), writes the journal entry, gives
// the number (BI-1, CR-1, TR-1, IV-1, CP-1) and locks the document; a posted document is corrected by reversing it.
// One deviation from the prototype: it saved with negative stock after a warning; Delta keeps the warning on the
// draft and refuses to post (S-6). Values on a draft are estimates at the current averages; posting fixes them.

interface KindCfg { route: string; list: string; title: string; kindKey: string; type: string; out?: boolean }
const KIND: Record<StockKind, KindCfg> = {
  beginning_inventory: { route: "/invoices/BeginningInventory", list: "beginningInventory", title: "biTitle", kindKey: "kindBeginning", type: "BeginningInventory" },
  corrupted: { route: "/invoices/Corrupted", list: "corruptedInvoices", title: "crTitle", kindKey: "kindCorrupted", type: "Corrupted", out: true },
  stock_transfer: { route: "/invoices/Transfer", list: "whTransfers", title: "trTitle", kindKey: "kindTransfer", type: "Transfer", out: true },
  inventory: { route: "/invoices/Inventory", list: "whInventory", title: "ivTitle", kindKey: "kindInventory", type: "Inventory" },
  compromise: { route: "/invoices/Inventory", list: "whInventory", title: "cpTitle", kindKey: "kindCompromise", type: "Compromise" }
};
const LINE_KINDS: StockKind[] = ["beginning_inventory", "corrupted", "stock_transfer"];
const COUNT_KINDS: StockKind[] = ["inventory", "compromise"];
const byType = (x: string | null): StockKind | undefined => (Object.keys(KIND) as StockKind[]).find((k) => KIND[k].type === x);
const editorPath = (k: StockKind, wh?: string) => `${isCountKind(k) ? "/warehouses/inventory/new" : "/invoices/create"}?type=${KIND[k].type}${wh ? `&wh=${wh}` : ""}`;
const showPath = (d: { id: string }) => `/invoices/show/${d.id}`;
const crumbs = (t: T, k: StockKind, extra: [string, string?][] = []): [string, string?][] => [[t("home"), "/home"], [t("warehouses")], [t(KIND[k].list), extra.length ? KIND[k].route : undefined], ...extra];
const label = (t: T, d: Pick<StockDocListDto, "code">) => d.code ?? t("journalDraft");
const nowLocal = nowRiyadh;
const canReverse = (d: StockDocListDto) => d.status === "posted" && !d.reversedAt && !d.cutover;
const D = Exact;
const ZERO: Exact.Dec = { n: 0n, s: 0 };
const signed = (x: string) => (x.startsWith("-") ? `−${fmtQty(x.slice(1))}` : /[1-9]/.test(x) ? `+${fmtQty(x)}` : "0");

/** Stock wording for the lifecycle errors; everything else as on every screen. */
function sErr(t: T, e: unknown): string {
  if (e instanceof ApiFailure) {
    if (e.code === "posted_locked") return t("stockPostedLocked");
    if (e.code === "already_reversed") return t("stockAlreadyReversed");
  }
  return errorText(t, e);
}

function StatusPill({ d }: { d: StockDocListDto }) {
  const { t } = useLang();
  if (d.status === "draft") return <span className="pill warn" data-testid="s-status">{t("journalDraft")}</span>;
  if (d.reversedAt) return <span className="pill" data-testid="s-status">{t("journalReversed")}</span>;
  if (d.cutover) return <span className="pill info" data-testid="s-status">{t("cutoverOpening")}</span>;
  return <span className="pill good" data-testid="s-status">{t("journalPosted")}</span>;
}
const KindPill = ({ k }: { k: StockKind }) => { const { t } = useLang(); return <span className={`pill ${k === "inventory" ? "info" : k === "compromise" ? "warn" : ""}`}>{t(KIND[k].kindKey)}</span>; };

/** Provisional-rules note on every stock screen while S-1 to S-7 are applied for staging only. */
const Provisional = () => { const { t } = useLang(); return <div className="dep-note" data-testid="s-provisional"><Icon name="info" /><span>{t("stockProvisional")}</span></div>; };

function useOptions() {
  const [opts, setOpts] = useState<StockOptionsDto | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { api<StockOptionsDto>("GET", "/stock/options").then(setOpts, () => setFailed(true)); }, []);
  return { opts, failed };
}

// ---------------------------------------------------------------- lists

/** /invoices/BeginningInventory, /invoices/Corrupted, /invoices/Transfer, and /invoices/Inventory (counts and compromises). */
export function StockDocs({ kind }: { kind: StockKind }) { return <DocList key={kind} kind={kind} />; }

function DocList({ kind }: { kind: StockKind }) {
  const { t, L } = useLang();
  const s = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const C = KIND[kind];
  const count = isCountKind(kind), transfer = kind === "stock_transfer";
  // The counts list shows both count kinds the user may view, with the prototype's tabs.
  const kinds = count ? COUNT_KINDS.filter((k) => s.can(STOCK_KEYS[k].view)) : [kind];
  const [tab, setTab] = useState<"all" | StockKind>("all");
  const { opts } = useOptions();
  const [data, setData] = useState<StockDocsDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [deleting, setDeleting] = useState<StockDocListDto | null>(null);
  const [reversing, setReversing] = useState<StockDocListDto | null>(null);
  const branch = filters.branchId || (s.branch !== "all" ? s.branch : "");
  const kindsQ = (tab === "all" ? kinds : [tab]).join(",");
  const load = useCallback(async () => {
    setFailed(null);
    const q = new URLSearchParams(Object.entries({ ...filters, branchId: branch, kinds: kindsQ }).filter(([, v]) => v)).toString();
    try { setData(await api<StockDocsDto>("GET", `/stock-documents?${q}`)); } catch (e) { setFailed(errorText(t, e)); }
  }, [filters, branch, kindsQ, t]);
  useEffect(() => { void load(); }, [load]);
  async function remove(d: StockDocListDto) {
    try { await api("DELETE", `/stock-documents/${d.id}`); toast(t("journalDraftDeletedOk")); } catch (e) { toast(sErr(t, e)); }
    await load();
  }
  async function postIt(d: StockDocListDto) {
    try { const p = await api<StockDocDto>("POST", `/stock-documents/${d.id}/post`); toast(t("stockPostedOk", { x: p.code ?? "", total: amt(p.total) })); }
    catch (e) { toast(Object.values(fieldErrorsOf(t, e))[0] ?? sErr(t, e)); }
    await load();
  }
  const columns: Column<StockDocListDto>[] = [
    { k: "no", label: t("invoiceNo"), cell: (d) => <Link to={showPath(d)} className="num">{label(t, d)}</Link>, csv: (d) => label(t, d) },
    { k: "status", label: t("status"), cell: (d) => <StatusPill d={d} />, csv: (d) => d.status },
    ...(count ? [{ k: "kind", label: t("kind"), cell: (d: StockDocListDto) => <KindPill k={d.kind} />, csv: (d: StockDocListDto) => t(KIND[d.kind].kindKey) }] : []),
    { k: "wh", label: t(transfer ? "fromWarehouse" : "warehouse"), cell: (d) => L(d.warehouse), csv: (d) => L(d.warehouse) },
    ...(transfer ? [{ k: "to", label: t("toWarehouse"), cell: (d: StockDocListDto) => L(d.toWarehouse), csv: (d: StockDocListDto) => L(d.toWarehouse) }] : []),
    { k: "by", label: t("by"), cell: (d) => d.createdByName ?? "–", csv: (d) => d.createdByName ?? "" },
    { k: "grand", label: t(count ? "diffValue" : "grandTotal"), end: true, cell: (d) => amt(d.total), csv: (d) => amt(d.total) },
    { k: "date", label: t("date"), cell: (d) => <span className="num">{when(d.docAt)}</span>, csv: (d) => when(d.docAt) }
  ];
  const lock = (k: string) => (s.can(k) ? {} : lockTag(t));
  const addKey = STOCK_KEYS[kind].add;
  const newBtn = s.can(addKey) || (count && s.can(STOCK_KEYS.compromise.add))
    ? <button className="btn btn-primary" onClick={() => nav(editorPath(count && !s.can(addKey) ? "compromise" : kind))} data-testid="s-new"><Icon name="plus" />{t("newInvoice")}</button>
    : <LockedButton label={t("newInvoice")} title={t("noPermissionFor", { p: addKey })} />;
  const search = [
    { k: "from", label: t("fromDate"), type: "text" as const, hint: "YYYY-MM-DD" }, { k: "to", label: t("toDate"), type: "text" as const, hint: "YYYY-MM-DD" },
    { k: "status", label: t("status"), type: "select" as const, options: [["draft", t("journalDraft")], ["posted", t("journalPosted")]] as [string, string][] },
    { k: "branchId", label: t("branch"), type: "select" as const, options: s.branches.map((b) => [b.id, L(b)] as [string, string]) },
    { k: "warehouseId", label: t("warehouse"), type: "select" as const, options: (opts?.warehouses ?? []).map((w) => [w.id, L(w)] as [string, string]) },
    { k: "q", label: `${t("invoiceNo")} / ${t("note")}`, type: "text" as const }
  ];
  const tabs = count && kinds.length > 1 ? (
    <div className="ltb-above"><div className="seg" role="tablist" aria-label={t("kind")}>
      {(["all", ...COUNT_KINDS] as const).map((k) => <button key={k} type="button" role="tab" aria-selected={tab === k} className={`seg-btn ${tab === k ? "on" : ""}`} onClick={() => setTab(k)} data-testid={`s-tab-${k}`}>{k === "all" ? t("all") : t(KIND[k].kindKey)}</button>)}
    </div></div>
  ) : undefined;
  return (
    <Shell navKey={C.route}>
      <PageHead crumbs={crumbs(t, kind)} title={t(C.list)}
        sub={data ? <>{t("journalPostedTotal")}: <b className="num" data-testid="s-total">{amt(data.total)}</b> · {t("journalDrafts")}: <b className="num" data-testid="s-drafts">{data.drafts}</b></> : undefined} />
      <Provisional />
      <ListView rows={data?.documents ?? null} error={failed} columns={columns} file={C.list} testId="stock-docs-table" onRefresh={() => void load()}
        search={search} filters={filters} onFilters={setFilters} newButton={newBtn} above={tabs}
        quick={(d) => `${d.code ?? ""} ${d.note} ${L(d.warehouse)} ${L(d.toWarehouse)} ${d.createdByName ?? ""}`}
        empty={{ icon: "warehouse", title: t("stockDocEmpty"), body: t("stockDocEmptyBody") }}
        actions={(d) => {
          const K = STOCK_KEYS[d.kind];
          return d.status === "draft" ? [
            { label: t("view"), icon: "eye", run: () => nav(showPath(d)) },
            { label: t("editInvoice"), icon: "edit", run: () => nav(`/invoices/edit/${d.id}`), ...lock(K.update) },
            { label: t("journalPost"), icon: "check", run: () => void postIt(d), ...lock(K.add) },
            { label: "", sep: true },
            { label: t("journalDeleteDraft"), icon: "trash", danger: true, run: () => setDeleting(d), ...lock(K.del) }
          ] : [
            { label: t("view"), icon: "eye", run: () => nav(showPath(d)) },
            ...(canReverse(d) ? [{ label: t("journalReverse"), icon: "undo" as const, run: () => setReversing(d), ...(s.can(K.add) && s.can(K.del) ? {} : lockTag(t)) }] : [])
          ];
        }} />
      {deleting && <Confirm title={t("confirmDelete")} body={t("stockDeleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => { const d = deleting; setDeleting(null); void remove(d); }} />}
      {reversing && <ReverseDialog d={reversing} onClose={() => setReversing(null)} onDone={(x) => { setReversing(null); nav(showPath(x)); }} />}
    </Shell>
  );
}

function ReverseDialog({ d, onClose, onDone }: { d: StockDocListDto; onClose(): void; onDone(x: StockDocDto): void }) {
  const { t } = useLang();
  const toast = useToast();
  const [at, setAt] = useState(() => (nowLocal() < d.docAt ? d.docAt : nowLocal()));
  const [statement, setStatement] = useState(() => t("stockReversalOf", { x: d.code ?? "" }));
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit() {
    setBusy(true); setAlert(null);
    try {
      const body: StockDocReverseInput = { reversedAt: at, statement };
      const x = await api<StockDocDto>("POST", `/stock-documents/${d.id}/reverse`, body);
      toast(t("stockReversedOk", { x: d.code ?? "" }));
      onDone(x);
    } catch (err) { const f = fieldErrorsOf(t, err); setErrs(f); setAlert(f.reversedAt ?? f.statement ?? sErr(t, err)); } finally { setBusy(false); }
  }
  return (
    <Modal title={`${t("journalReverse")} ${d.code ?? ""}`} sub={t("stockReverseSub")} onClose={onClose} onSubmit={() => void submit()} alert={alert} busy={busy} saveLabel={t("journalReverse")}>
      <Field label={t("dateTime")} req htmlFor="rs-dt" error={errs.reversedAt}><input id="rs-dt" className="control num" style={{ textAlign: "start" }} type="datetime-local" value={at} min={d.docAt} onChange={(x) => setAt(x.target.value)} /></Field>
      <Field label={t("statement")} htmlFor="rs-st" full error={errs.statement}><input id="rs-st" className="control" value={statement} maxLength={500} onChange={(x) => setStatement(x.target.value)} /></Field>
    </Modal>
  );
}

// ---------------------------------------------------------------- editor

interface Line { key: number; itemId: string; unitId: string; qty: string; actual: string; price: string; sel: boolean }
let seq = 0;
const blank = (): Line => ({ key: ++seq, itemId: "", unitId: "", qty: "", actual: "", price: "", sel: false });
type Opts = StockOptionsDto;
const unitOf = (o: Opts | null, l: Pick<Line, "itemId" | "unitId">) => o?.items.find((i) => i.id === l.itemId)?.units.find((u) => u.id === l.unitId);
const position = (o: Opts | null, wh: string, itemId: string) => o?.positions.find((p) => p.warehouseId === wh && p.itemId === itemId);
const num = (x: string, spec: typeof QUANTITY): Exact.Dec | null => { if (!x.trim()) return null; const r = parseDecimal(x, spec); return "error" in r ? null : D.dec(r.value); };
/** Quantity on hand in the entered unit (base quantity ÷ pieces), three decimals. */
function onHand(o: Opts | null, wh: string, l: Pick<Line, "itemId" | "unitId">): Exact.Dec {
  const p = position(o, wh, l.itemId), u = unitOf(o, l);
  return p && u ? D.div(D.dec(p.qty), D.dec(u.pieces), 3) : ZERO;
}
/** Average price per entered unit (value ÷ quantity × pieces), four decimals; null with no stock. */
function average(o: Opts | null, wh: string, l: Pick<Line, "itemId" | "unitId">): Exact.Dec | null {
  const p = position(o, wh, l.itemId), u = unitOf(o, l);
  if (!p || !u || !/[1-9]/.test(p.qty)) return null;
  return D.div(D.mul(D.dec(p.value), D.dec(u.pieces)), D.dec(p.qty), 4);
}
const money = (x: Exact.Dec) => amt(D.fmt(D.roundHalfAway(x, 2), 2));

/**
 * /invoices/create?type=BeginningInventory|Corrupted|Transfer, /warehouses/inventory/new?type=Inventory|Compromise and
 * /invoices/edit/:id. Other invoice types keep their place until their modules are built.
 */
export function StockCreate() {
  const [qs] = useSearchParams();
  const kind = byType(qs.get("type"));
  if (!kind || !LINE_KINDS.includes(kind)) return <NotBuilt />;
  return <StockEditor key={kind} kind={kind} />;
}
export function CountCreate() {
  const [qs] = useSearchParams();
  const kind = byType(qs.get("type"));
  return <StockEditor key={kind ?? "inventory"} kind={kind && COUNT_KINDS.includes(kind) ? kind : "inventory"} />;
}
export function StockEdit() {
  const { id } = useParams();
  const { t } = useLang();
  const [d, setD] = useState<StockDocDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => { setD(null); api<StockDocDto>("GET", `/stock-documents/${id}`).then(setD, (e) => setFailed(e instanceof ApiFailure && e.status === 404 ? t("stockDocNotFound") : errorText(t, e))); }, [id, t]);
  if (!d) return <Loading failed={failed} />;
  return <StockEditor key={d.id} kind={d.kind} src={d} />;
}
/** The legacy hidden routes crashed during the audit; the prototype sends them to the working editors. */
export const TransferRedirect = () => <Navigate to="/invoices/create?type=Transfer" replace />;
export const DepreciationRedirect = () => <Navigate to="/invoices/create?type=Corrupted" replace />;

function Loading({ failed }: { failed: string | null }) {
  const { t } = useLang();
  return (
    <Shell navKey="/invoices/BeginningInventory"><PageHead crumbs={[[t("home"), "/home"], [t("warehouses")]]} title={t("invoiceData")} />
      <section className="card"><div className={`state-box ${failed ? "error" : ""}`}>{failed ? <><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p></> : <p>{t("loading")}</p>}</div></section>
    </Shell>
  );
}

function StockEditor({ kind: initial, src }: { kind: StockKind; src?: StockDocDto }) {
  const { t, L } = useLang();
  const s = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const [qs] = useSearchParams();
  const { opts, failed: optsFailed } = useOptions();
  const [kind, setKind] = useState<StockKind>(initial);
  const count = isCountKind(kind), transfer = kind === "stock_transfer", bi = kind === "beginning_inventory";
  const K = STOCK_KEYS[kind], C = KIND[kind];
  const [at, setAt] = useState(src?.docAt ?? nowLocal());
  const [wh, setWh] = useState(src?.warehouse.id ?? qs.get("wh") ?? "");
  const [to, setTo] = useState(src?.toWarehouse?.id ?? "");
  const [note, setNote] = useState(src?.note ?? "");
  const [lines, setLines] = useState<Line[]>(() => src?.lines.map((l) => ({ key: ++seq, itemId: l.item.id, unitId: l.unit.id, qty: l.qty ? fmtQty(l.qty) : "", actual: l.actualQty ? fmtQty(l.actualQty) : "", price: l.enteredPrice ? dec(l.enteredPrice) : "", sel: false })) ?? []);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState<string | null>(null);
  const [bc, setBc] = useState("");
  // A warehouse from ?wh= that is outside the user's scope is dropped once the options arrive.
  useEffect(() => { if (opts && wh && !opts.warehouses.some((w) => w.id === wh)) setWh(""); }, [opts, wh]);
  const items = opts?.items ?? [];
  const fail = (m: string) => setAlert(m);
  const sysOf = (l: Line) => onHand(opts, wh, l);
  const set = (i: number, f: keyof Line, v: string | boolean) => { setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [f]: v } : l))); setErrs({}); };
  /** Item or unit chosen: a count starts its actual quantity at the system quantity (prototype). */
  const choose = (l: Line, itemId: string, unitId?: string): Line => {
    const it = items.find((x) => x.id === itemId);
    const n = { ...l, itemId, unitId: it ? unitId ?? it.units[0]!.id : "" };
    if (count && it) n.actual = fmtQty(D.fmt(onHand(opts, wh, n), 3));
    return n;
  };
  const pick = (i: number, itemId: string, unitId?: string) => {
    if (!wh) { fail(t("selectWarehouseFirst")); return; }
    const it = items.find((x) => x.id === itemId);
    const u = unitId ?? it?.units[0]?.id;
    const dup = lines.find((x, j) => j !== i && it && x.itemId === itemId && x.unitId === u);
    if (dup) { toast(t("alreadyListed", { x: L(it) })); return; }
    setLines((ls) => ls.map((l, j) => (j === i ? choose(l, itemId, unitId) : l))); setErrs({}); setAlert(null);
  };
  function scan(code: string) {
    const v = code.trim(); setBc("");
    if (!v) return;
    if (!wh) { fail(t("selectWarehouseFirst")); return; }
    let found: { itemId: string; unitId: string } | null = null;
    for (const it of items) for (const u of it.units) if (!found && u.barcodes.includes(v)) found = { itemId: it.id, unitId: u.id };
    if (!found) { toast(t("barcodeNotFound", { x: v })); return; }
    const f = found, it = items.find((x) => x.id === f.itemId)!;
    const at = lines.findIndex((l) => l.itemId === f.itemId && l.unitId === f.unitId);
    // Counts list each item once; the other documents add one per scan (prototype).
    if (at >= 0) {
      if (count) { toast(t("alreadyListed", { x: L(it) })); return; }
      setLines((ls) => ls.map((l, j) => (j === at ? { ...l, qty: D.fmt(D.add(num(l.qty, QUANTITY) ?? ZERO, D.dec("1"))) } : l)));
      return;
    }
    setLines((ls) => {
      const emptyAt = ls.findIndex((l) => !l.itemId);
      const n = { ...choose(emptyAt >= 0 ? ls[emptyAt]! : blank(), f.itemId, f.unitId), ...(count ? {} : { qty: "1" }) };
      return emptyAt >= 0 ? ls.map((l, j) => (j === emptyAt ? n : l)) : [...ls, n];
    });
  }
  function loadAll() {
    if (!wh) { fail(t("selectWarehouseFirst")); return; }
    const add = (opts?.positions ?? []).filter((p) => p.warehouseId === wh && /[1-9]/.test(p.qty))
      .map((p) => items.find((i) => i.id === p.itemId)).filter((it): it is NonNullable<typeof it> => !!it && !lines.some((l) => l.itemId === it.id && l.unitId === it.units[0]!.id))
      .map((it) => choose(blank(), it.id));
    setLines((ls) => [...ls.filter((l) => l.itemId), ...add]);
    toast(t("loadedN", { n: add.length }));
  }
  function changeWh(v: string) {
    if (v === wh) return;
    if (lines.some((l) => l.itemId)) { setChanging(v); return; }
    setWh(v);
  }

  // Estimates (the server fixes the values when it posts).
  const est = (l: Line): { value: Exact.Dec | null; diff: Exact.Dec | null; avg: Exact.Dec | null; sys: Exact.Dec; over: boolean } => {
    const avg = wh && l.itemId ? average(opts, wh, l) : null, sys = wh && l.itemId ? sysOf(l) : ZERO;
    if (count) {
      const a = num(l.actual, QUANTITY);
      const diff = a ? D.sub(a, sys) : null;
      const price = num(l.price, PRICE) ?? avg ?? ZERO;
      return { value: diff ? D.mul(diff, price) : null, diff, avg, sys, over: false };
    }
    const q = num(l.qty, QUANTITY);
    const price = bi ? num(l.price, PRICE) : avg;
    return { value: q && price ? D.mul(q, price) : q ? ZERO : null, diff: null, avg, sys, over: !!C.out && !!q && !!l.itemId && D.cmp(q, sys) > 0 };
  };
  const totals = useMemo(() => {
    let total = ZERO, counted = ZERO;
    for (const l of lines) {
      if (!l.itemId) continue;
      const e = est(l);
      if (e.value) total = D.add(total, D.roundHalfAway(e.value, 2));
      if (count) { const a = num(l.actual, QUANTITY); const p = num(l.price, PRICE) ?? e.avg; if (a && p) counted = D.add(counted, D.roundHalfAway(D.mul(a, p), 2)); }
    }
    return { total, counted };
  }, [lines, opts, wh, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  if (src && src.status === "posted") return (
    <Shell navKey={C.route}><PageHead crumbs={crumbs(t, kind, [[t("editInvoice")]])} title={`${t("editInvoice")} ${label(t, src)}`} />
      <section className="card"><div className="state-box" data-testid="posted-locked"><Icon name="lock" /><h3>{t("stockPostedLocked")}</h3><p>{t("stockPostedNote")}</p>
        <p><Link className="btn" to={showPath(src)}>{t(C.title)} {label(t, src)}</Link></p></div></section>
    </Shell>
  );
  if (!s.can(src ? K.update : K.add)) return (
    <Shell navKey={C.route}><PageHead crumbs={crumbs(t, kind, [[t(src ? "editInvoice" : "createInvoice")]])} title={t(src ? "editInvoice" : count ? "invCompTitle" : "createInvoice")} />
      <section className="card"><div className="state-box" data-testid="s-no-permission"><Icon name="lock" /><h3>{t("noPermissionFor", { p: src ? K.update : K.add })}</h3></div></section>
    </Shell>
  );

  async function save(andPost: boolean) {
    const used = lines.filter((l) => l.itemId);
    const e: Record<string, string> = {};
    const ix = (l: Line) => lines.indexOf(l);
    if (!at) { setErrs({ docAt: "x" }); fail(`${t("requiredField")}: ${t("invoiceDateTime")}`); return; }
    if (!wh) { setErrs({ warehouseId: "x" }); fail(t("selectWarehouse")); return; }
    if (transfer && !to) { setErrs({ toWarehouseId: "x" }); fail(`${t("requiredField")}: ${t("toWarehouse")}`); return; }
    if (transfer && to === wh) { setErrs({ toWarehouseId: "x" }); fail(t("sameWarehouse")); return; }
    if (!used.length) { fail(t("stockNeedLines")); return; }
    for (const l of used) {
      const bad = !l.unitId || (count ? num(l.actual, QUANTITY) === null : !(num(l.qty, QUANTITY)?.n ?? 0n)) || (bi && num(l.price, PRICE) === null) || (!!l.price.trim() && num(l.price, PRICE) === null);
      if (bad) e[`lines.${ix(l)}`] = "x";
    }
    if (Object.keys(e).length) { setErrs(e); fail(t(count ? "countLineInvalid" : "stockLineInvalid")); return; }
    if (count && used.every((l) => { const d = est(l).diff; return !d || d.n === 0n; })) { fail(t("noInventoryChanges")); return; }
    const body: StockDocInput & { kind?: StockKind } = {
      docAt: at, warehouseId: wh, toWarehouseId: transfer ? to : null, note, post: andPost,
      lines: used.map((l) => ({ itemId: l.itemId, itemUnitId: l.unitId, ...(count ? { actualQty: l.actual.trim() } : { qty: l.qty.trim() }), price: (bi || count) && l.price.trim() ? l.price.trim() : null }))
    };
    if (!src) body.kind = kind;
    setBusy(true); setAlert(null); setErrs({});
    try {
      const x = src ? await api<StockDocDto>("PUT", `/stock-documents/${src.id}`, body) : await api<StockDocDto>("POST", "/stock-documents", body);
      toast(x.status === "posted" ? t("stockPostedOk", { x: x.code ?? "", total: amt(x.total) }) : t("journalDraftSavedOk"));
      nav(showPath(x));
    } catch (err) {
      // Server field errors use the positions of the lines sent; map them back to the rows on screen.
      const f = fieldErrorsOf(t, err), back: Record<string, string> = {};
      for (const [k, v] of Object.entries(f)) { const m = /^lines\.(\d+)\.(.+)$/.exec(k); back[m ? `lines.${ix(used[Number(m[1])]!)}` : k] = v; }
      setErrs(back); fail(Object.values(back)[0] ?? sErr(t, err));
    } finally { setBusy(false); }
  }

  const whOpts = (opts?.warehouses ?? []).map((w) => <option key={w.id} value={w.id}>{L(w)}</option>);
  const kinds = (count ? COUNT_KINDS : LINE_KINDS).filter((k) => s.can(STOCK_KEYS[k].add));
  const anySel = lines.some((l) => l.sel);
  const cats = opts?.categories ?? [];
  const itemSelect = (l: Line, i: number) => (
    <select className="control" value={l.itemId} onChange={(e) => pick(i, e.target.value)} aria-label={t("itemName")} disabled={!opts}>
      <option value="">{t("choose")}</option>
      {cats.map((c) => { const xs = items.filter((it) => it.categoryId === c.id); return xs.length ? <optgroup key={c.id} label={L(c)}>{xs.map((it) => <option key={it.id} value={it.id}>{L(it)} · {it.code}</option>)}</optgroup> : null; })}
    </select>
  );
  const unitSelect = (l: Line, i: number) => {
    const it = items.find((x) => x.id === l.itemId);
    return <select className="control" value={l.unitId} onChange={(e) => pick(i, l.itemId, e.target.value)} aria-label={t("unitName")} disabled={!it}>
      {it ? it.units.map((u) => <option key={u.id} value={u.id}>{L(u)}</option>) : <option>–</option>}
    </select>;
  };
  const input = (l: Line, i: number, f: "qty" | "actual" | "price", lab: string, ph?: string) => (
    <input className="control num" inputMode="decimal" dir="ltr" value={l[f]} placeholder={ph} onChange={(e) => set(i, f, e.target.value)} aria-label={lab} data-testid={`s-${f}-${i}`} />
  );
  const th = count ? [t("itemName"), t("unitName"), t("price"), t("qty"), t("actualQty"), t("difference"), t("diffValue")] : [t("itemName"), t("unitName"), t("qty"), t("price"), t("total")];
  const endFrom = 2;
  const rows = lines.map((l, i) => {
    const e = est(l), err = errs[`lines.${i}`];
    const head = <>
      <td className="w-chk"><input type="checkbox" checked={l.sel} onChange={(x) => set(i, "sel", x.target.checked)} aria-label={t("selectRow")} /></td><td className="num muted">{i + 1}</td>
      <td className="w-item">{itemSelect(l, i)}</td><td className="w-unit">{unitSelect(l, i)}</td>
    </>;
    const del = <td className="end"><button className="btn btn-sm btn-icon btn-danger-ghost" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} aria-label={t("delete")}><Icon name="trash" /></button></td>;
    const avgText = e.avg ? dec(D.fmt(e.avg, 4)) : "–";
    if (count) return (
      <tr key={l.key} className={err ? "row-err" : ""} title={err && err !== "x" ? err : undefined}>{head}
        <td className="w-amt">{input(l, i, "price", t("price"), e.avg ? avgText : undefined)}</td>
        <td className="end num w-sys" data-testid={`s-sys-${i}`}>{l.itemId ? fmtQty(D.fmt(e.sys, 3)) : "–"}</td>
        <td className="w-amt">{input(l, i, "actual", t("actualQty"))}</td>
        <td className="end num"><b className={e.diff && e.diff.n > 0n ? "qty-in" : e.diff && e.diff.n < 0n ? "qty-out" : ""} data-testid={`s-diff-${i}`}>{e.diff ? signed(D.fmt(e.diff, 3)) : "–"}</b></td>
        <td className="end num" data-testid={`s-lv-${i}`}>{e.value ? money(e.value) : "–"}</td>{del}
      </tr>
    );
    return (
      <tr key={l.key} className={err ? "row-err" : ""} title={err && err !== "x" ? err : undefined}>{head}
        <td className="w-amt">{input(l, i, "qty", t("qty"))}
          {l.itemId && wh && !bi && <div className={`hint num ${e.over ? "over" : ""}`} title={t("exceedsStock")} data-testid={`s-avail-${i}`}>{t("availableN", { n: fmtQty(D.fmt(e.sys, 3)) })}</div>}</td>
        <td className="w-amt">{bi ? input(l, i, "price", t("price")) : <span className="num muted" title={t("averagePrice")}>{avgText}</span>}</td>
        <td className="end num"><b data-testid={`s-lv-${i}`}>{e.value ? money(e.value) : "–"}</b></td>{del}
      </tr>
    );
  });
  const bar = (
    <div className="action-bar"><div className="ab-stats">
      <div><div className="k">{t("invoiceNo")}</div><div className="v num">{src ? label(t, src) : t("journalDraft")}</div></div>
      {count && <div><div className="k">{t("countedValue")}</div><div className="v num" data-testid="s-counted">{money(totals.counted)}</div></div>}
      <div><div className="k">{t(count ? "diffValue" : "grandTotal")}</div><div className="v num" data-testid="s-grand">{money(totals.total)}</div></div>
    </div><div className="ab-actions">
      <Link className="btn" to={src ? showPath(src) : C.route}>{t("cancel")}</Link>
      <button className="btn" onClick={() => void save(false)} disabled={busy || optsFailed} data-testid="s-save-draft"><Icon name="edit" />{t("journalSaveDraft")}</button>
      {s.can(K.add) ? <button className="btn btn-primary" onClick={() => void save(true)} disabled={busy || optsFailed} data-testid="s-post"><Icon name="check" />{busy ? t("saving") : t("voucherSaveAndPost")}</button>
        : <LockedButton label={t("voucherSaveAndPost")} title={t("noPermissionFor", { p: K.add })} />}
    </div></div>
  );
  const title = src ? `${t("editInvoice")} ${label(t, src)}` : t(count ? "invCompTitle" : "createInvoice");
  const whField = (id: string, value: string, onChange: (v: string) => void, lab: string, err?: string) => (
    <div className={`field ${err ? "invalid" : ""}`}><label className="req" htmlFor={id}>{lab}</label>
      <select id={id} className="control" value={value} onChange={(e) => onChange(e.target.value)} disabled={!opts}><option value="">{t("choose")}</option>{whOpts}</select></div>
  );
  return (
    <Shell navKey={C.route} bar={bar}>
      <PageHead crumbs={crumbs(t, kind, [[src ? label(t, src) : t(count ? "newInventory" : "createInvoice")]])} title={title} badge={<KindPill k={kind} />} />
      <Provisional />
      <div className="inv-stack">
        <Card title={src ? t("invoiceData") : title}><div className="form-grid">
          {!src && kinds.length > 1 && <div className="field span-3"><span className="label">{t("invoiceType")}</span>
            <div className="segmented se-type" role="radiogroup" aria-label={t("invoiceType")}>{kinds.map((k) => (
              <label key={k}><input type="radio" name="se-type" value={k} checked={k === kind} onChange={() => { setKind(k); setErrs({}); setAlert(null); if (isCountKind(k) !== count) setLines([]); }} data-testid={`s-type-${KIND[k].type}`} /><span>{t(KIND[k].kindKey)}</span></label>
            ))}</div></div>}
          <div className={`field ${errs.docAt ? "invalid" : ""}`}><label className="req" htmlFor="se-dt">{t("invoiceDateTime")}</label>
            <input id="se-dt" className="control num" style={{ textAlign: "start" }} type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} /></div>
          {whField("se-wh", wh, changeWh, t(transfer ? "fromWarehouse" : "warehouse"), errs.warehouseId)}
          {transfer && whField("se-to", to, setTo, t("toWarehouse"), errs.toWarehouseId)}
          <div className={`field ${transfer ? "span-3" : ""}`}><label htmlFor="se-note">{t("note")}</label><input id="se-note" className="control" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} /></div>
        </div></Card>
        <Card title={t("invoiceItems")} count={lines.length} raw>
          <div className="lines-toolbar">
            <div className="barcode input-group"><span className="lead"><Icon name="barcode" /></span>
              <input className="control num" style={{ textAlign: "start" }} id="se-bcin" value={bc} onChange={(e) => setBc(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scan(bc); } }} placeholder={t("searchBarcode")} aria-label={t("searchBarcode")} data-testid="s-barcode" /></div>
            <span className="spacer" />
            {count && <button className="btn btn-sm" onClick={loadAll} data-testid="s-load-all"><Icon name="box" />{t("loadAllItems")}</button>}
            <button className="btn btn-sm" onClick={() => (wh ? setLines((ls) => [...ls, blank()]) : fail(t("selectWarehouseFirst")))} data-testid="s-add-line"><Icon name="plus" />{t("addLine")}</button>
            <button className="btn btn-sm btn-danger-ghost" disabled={!anySel} onClick={() => setLines((ls) => ls.filter((l) => !l.sel))}><Icon name="trash" />{t("deleteSelected")}</button>
          </div>
          <div className="table-wrap"><table className="tbl lines-table je-lines se-lines" data-testid="s-lines">
            <thead><tr><th className="w-chk"><input type="checkbox" checked={!!lines.length && lines.every((l) => l.sel)} onChange={(e) => setLines((ls) => ls.map((l) => ({ ...l, sel: e.target.checked })))} aria-label={t("selectAll")} /></th><th>#</th>
              {th.map((h, i) => <th key={h} className={i >= endFrom ? "end" : ""}>{h}</th>)}<th className="end">{t("delete")}</th></tr></thead>
            <tbody>{rows.length ? rows : <tr><td colSpan={th.length + 3}><div className="empty sm"><Icon name="barcode" /><div>{t(wh ? "stockLinesHint" : "selectWarehouseFirst")}</div></div></td></tr>}</tbody>
          </table></div>
          <p className="hint pad">{t(count ? "countPriceHint" : bi ? "biPriceHint" : "averagePriceHint")} {t("stockEstimateNote")}</p>
          <div className={`dlg-alert ${alert ? "show" : ""}`} role="alert" data-testid="s-alert">{alert && <><Icon name="alert" /><span>{alert}</span></>}</div>
        </Card>
      </div>
      {changing !== null && <Confirm title={t("changeWhTitle")} body={t("changeWhLoses")} okLabel={t("changeWh")} onClose={() => setChanging(null)} onOk={() => { setWh(changing); setLines([]); setChanging(null); }} />}
    </Shell>
  );
}

// ---------------------------------------------------------------- document page

/** /invoices/show/:id of a stock document. */
export function StockShow() {
  const { id } = useParams();
  const { t, L } = useLang();
  const s = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const [d, setD] = useState<StockDocDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setD(null); setFailed(null); api<StockDocDto>("GET", `/stock-documents/${id}`).then(setD, (e) => setFailed(e instanceof ApiFailure && e.status === 404 ? t("stockDocNotFound") : errorText(t, e))); }, [id, t]);
  if (!d) return <Loading failed={failed} />;
  const C = KIND[d.kind], K = STOCK_KEYS[d.kind], draft = d.status === "draft", count = isCountKind(d.kind), transfer = d.kind === "stock_transfer";
  async function postIt() {
    setBusy(true);
    try { const p = await api<StockDocDto>("POST", `/stock-documents/${d!.id}/post`); toast(t("stockPostedOk", { x: p.code ?? "", total: amt(p.total) })); setD(p); }
    catch (err) { toast(Object.values(fieldErrorsOf(t, err))[0] ?? sErr(t, err)); } finally { setBusy(false); }
  }
  async function remove() {
    setDeleting(false);
    try { await api("DELETE", `/stock-documents/${d!.id}`); toast(t("journalDraftDeletedOk")); nav(C.route); } catch (err) { toast(sErr(t, err)); }
  }
  const actions = draft ? <>
    {s.can(K.update) && <Link className="btn" to={`/invoices/edit/${d.id}`}><Icon name="edit" />{t("editInvoice")}</Link>}
    {s.can(K.del) && <button className="btn btn-danger-ghost" onClick={() => setDeleting(true)}><Icon name="trash" />{t("journalDeleteDraft")}</button>}
    {s.can(K.add) && <button className="btn btn-primary" disabled={busy} onClick={() => void postIt()} data-testid="s-show-post"><Icon name="check" />{t("journalPost")}</button>}
  </> : <>
    {canReverse(d) && s.can(K.add) && s.can(K.del) && <button className="btn" onClick={() => setReversing(true)} data-testid="s-show-reverse"><Icon name="undo" />{t("journalReverse")}</button>}
    <button className="btn btn-primary" onClick={() => window.print()}><Icon name="printer" />{t("print")}</button>
  </>;
  const je = (r: { id: string; number: string }) => <Link className="num" to={`/journals/show/${r.id}`}>{r.number}</Link>;
  const kv: [string, ReactNode, string?][] = [
    [t("invoiceNo"), <b className="num">{label(t, d)}</b>], [t("status"), <StatusPill d={d} />], [t("invoiceDateTime"), <span className="num">{when(d.docAt)}</span>],
    [t("invoiceType"), t(C.kindKey)], [t(transfer ? "fromWarehouse" : "warehouse"), L(d.warehouse)]
  ];
  if (transfer) kv.push([t("toWarehouse"), L(d.toWarehouse)]);
  kv.push([t("branch"), L(d.branch)], [t("by"), d.createdByName ?? "–"]);
  if (d.postedAt) kv.push([t("journalPostedBy"), <>{d.postedByName ?? "–"} · <span className="num">{instant(d.postedAt)}</span></>]);
  if (!draft) kv.push([t("voucherJournal"), d.journalEntry ? je(d.journalEntry) : <span className="muted" data-testid="s-no-journal">{t(d.cutover ? "cutoverNoJournal" : "stockNoJournal")}</span>]);
  if (d.reversedAt) kv.push([t("voucherReversalEntry"), d.reversal ? je(d.reversal) : <span className="num">{when(d.reversalAt ?? "")}</span>]);
  if (d.legacyNumber) kv.push([t("legacyNumber"), <span className="num">{d.legacyNumber}</span>]);
  kv.push([t("note"), d.note || <span className="muted">–</span>, "span-2"]);
  const counted = d.lines.reduce((a, l) => (l.actualQty ? D.add(a, D.roundHalfAway(D.mul(D.dec(l.actualQty), D.dec(l.price)), 2)) : a), ZERO);
  return (
    <Shell navKey={C.route}>
      <PageHead crumbs={crumbs(t, d.kind, [[label(t, d)]])} title={`${t(C.title)} ${label(t, d)}`} badge={<KindPill k={d.kind} />} actions={actions} />
      <Provisional />
      {draft && <div className="dep-note" data-testid="draft-note"><Icon name="alert" /><span>{t("stockDraftNote")}</span></div>}
      {!draft && <div className="dep-note" data-testid="posted-note"><Icon name="lock" /><span>{t(d.cutover ? "cutoverOpeningNote" : "stockPostedNote")}</span></div>}
      {d.warnings.map((w) => (
        <div key={w.lineNo} className="dep-note" data-testid="s-warning"><Icon name="alert" /><span>{t("stockWarning", { n: w.lineNo, q: fmtQty(w.qty), a: fmtQty(w.available) })}</span></div>
      ))}
      <div className="detail-grid"><div className="detail-main">
        <Card title={t("invoiceData")}><dl className="kv" style={{ ["--kv-cols" as string]: 3 }} data-testid="stock-data">{kv.map(([k, x, cls]) => <div key={k} className={cls}><dt>{k}</dt><dd>{x}</dd></div>)}</dl></Card>
        <Card title={t("invoiceItems")} count={d.lines.length} raw>
          <div className="table-wrap"><table className="tbl detail-lines" data-testid="stock-lines">
            <thead><tr><th>#</th><th>{t("itemName")}</th><th>{t("unitName")}</th>
              {count ? <><th className="end">{t("price")}</th><th className="end">{t("qty")}</th><th className="end">{t("actualQty")}</th><th className="end">{t("difference")}</th><th className="end">{t("diffValue")}</th></>
                : <><th className="end">{t("qty")}</th><th className="end">{t("price")}</th><th className="end">{t("total")}</th></>}</tr></thead>
            <tbody>{d.lines.map((l) => (
              <tr key={l.lineNo}>
                <td className="num muted">{l.lineNo}</td>
                <td><div className="cell-name">{L(l.item)}</div><div className="hint num">{l.item.code}</div></td>
                <td className="cell-unit">{L(l.unit)}</td>
                {count ? <>
                  <td className="end num">{dec(l.price, 2)}</td><td className="end num">{l.systemQty ? fmtQty(l.systemQty) : "–"}</td><td className="end num">{l.actualQty ? fmtQty(l.actualQty) : "–"}</td>
                  <td className="end num"><span className={l.difference?.startsWith("-") ? "qty-out" : l.difference && /[1-9]/.test(l.difference) ? "qty-in" : ""}>{l.difference ? signed(l.difference) : "–"}</span></td>
                  <td className="end num"><b>{amt(l.value)}</b></td>
                </> : <>
                  <td className="end num">{l.qty ? fmtQty(l.qty) : "–"}{l.available !== null && C.out && <div className="hint num">{t("availableN", { n: fmtQty(l.available) })}</div>}</td>
                  <td className="end num">{d.cutover ? "–" : dec(l.price, 2)}</td><td className="end num"><b>{amt(l.value)}</b></td>
                </>}
              </tr>
            ))}</tbody>
          </table></div>
          <div className="totals-band q">
            {count && <div><span>{t("countedValue")}</span><b className="num">{money(counted)}</b></div>}
            <div><span>{t("linesCount")}</span><b className="num">{d.lines.length}</b></div>
            <div className="g"><span>{t(count ? "diffValue" : "grandTotal")}</span><b className="num" data-testid="s-doc-total">{amt(d.total)} <small>{t("currency")}</small></b></div>
          </div>
          {draft && <p className="hint pad">{t("stockEstimateNote")}</p>}
        </Card>
      </div>
      <aside className="detail-side"><Card title={t("attachments")}><div className="dep-note" data-testid="pending-field"><Icon name="lock" /><span>{t("waitsFor", { m: t("modFileStore") })}</span></div></Card></aside></div>
      {deleting && <Confirm title={t("confirmDelete")} body={t("stockDeleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(false)} onOk={() => void remove()} />}
      {reversing && <ReverseDialog d={d} onClose={() => setReversing(false)} onDone={(x) => { setReversing(false); setD(x); }} />}
    </Shell>
  );
}

// ---------------------------------------------------------------- stock on hand

/** /warehouse-stock?wh=: quantity, average price and value per item per warehouse, in base units (prototype). */
export function StockOnHand() {
  const { t, L } = useLang();
  const s = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const [qs, setQs] = useSearchParams();
  const wh = qs.get("wh") ?? "";
  const { opts } = useOptions();
  const [data, setData] = useState<StockOnHandDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [bcItem, setBcItem] = useState<{ id: string; code: string; name: string } | null>(null);
  const [bc, setBc] = useState("");
  const load = useCallback(async () => {
    setFailed(null);
    const q = new URLSearchParams(Object.entries({ warehouseId: wh, categoryId: filters.cat ?? "", q: filters.item ?? "", itemId: bcItem?.id ?? "" }).filter(([, v]) => v)).toString();
    try { setData(await api<StockOnHandDto>("GET", `/stock/on-hand${q ? "?" + q : ""}`)); } catch (e) { setFailed(errorText(t, e)); }
  }, [wh, filters.cat, filters.item, bcItem, t]);
  useEffect(() => { void load(); }, [load]);
  // The top bar's branch picker narrows the warehouses, as in the prototype.
  const warehouses = (opts?.warehouses ?? []).filter((w) => s.branch === "all" || w.branchId === s.branch);
  const rows = (data?.rows ?? null)?.filter((r) => warehouses.some((w) => w.id === r.warehouse.id)) ?? null;
  function scan(code: string) {
    const v = code.trim();
    if (!v) { setBcItem(null); return; }
    const it = opts?.items.find((i) => i.units.some((u) => u.barcodes.includes(v)));
    if (!it) { toast(t("barcodeNotFound", { x: v })); return; }
    setBcItem({ id: it.id, code: v, name: L(it) });
  }
  const low = (r: StockRowDto) => /[1-9]/.test(r.item.minQty) && D.cmp(D.dec(r.qty), D.dec(r.item.minQty)) < 0;
  const columns: Column<StockRowDto & { id: string }>[] = [
    { k: "wh", label: t("warehouse"), cell: (r) => L(r.warehouse), csv: (r) => L(r.warehouse) },
    { k: "item", label: t("itemName"), cell: (r) => <><Link className="cell-name" to={`/items/show/${r.item.id}`}>{L(r.item)}</Link><div className="hint num">{r.item.code}</div></>, csv: (r) => L(r.item) },
    { k: "cat", label: t("category"), cell: (r) => L(r.category), csv: (r) => L(r.category) },
    { k: "qty", label: t("qty"), end: true, cell: (r) => <><span data-testid="s-onhand-qty">{fmtQty(r.qty)}</span> <small className="muted">{L(r.baseUnit)}</small>{low(r) && <div className="hint warn">{t("belowMin")}</div>}</>, csv: (r) => fmtQty(r.qty) },
    { k: "avg", label: t("averagePrice"), end: true, cell: (r) => dec(r.averageCost), csv: (r) => dec(r.averageCost) },
    { k: "total", label: t("total"), end: true, cell: (r) => amt(r.value), csv: (r) => amt(r.value) }
  ];
  const seg = (
    <div className="ltb-above"><div className="seg" role="tablist" aria-label={t("warehouse")}>
      <button type="button" className={`seg-btn ${!wh ? "on" : ""}`} onClick={() => setQs({}, { replace: true })}>{t("all")}</button>
      {warehouses.map((w) => <button type="button" key={w.id} className={`seg-btn ${wh === w.id ? "on" : ""}`} onClick={() => setQs({ wh: w.id }, { replace: true })} data-testid={`s-seg-${w.id}`}>{L(w)}</button>)}
    </div></div>
  );
  const above = <>
    <div className="ltb-above">
      <div className="barcode input-group"><span className="lead"><Icon name="barcode" /></span>
        <input className="control num" style={{ textAlign: "start" }} value={bc} onChange={(e) => setBc(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); scan(bc); } }} placeholder={t("searchBarcode")} aria-label={t("searchBarcode")} data-testid="s-onhand-barcode" /></div>
      {bcItem && <span className="fchip">{t("itemName")}: <b>{bcItem.name}</b><button onClick={() => { setBcItem(null); setBc(""); }} aria-label={t("removeFilter")}><Icon name="x" /></button></span>}
      <span className="hint">{t("baseUnitNote")}</span>
    </div>{seg}
  </>;
  const whName = wh ? L(opts?.warehouses.find((w) => w.id === wh)) : "";
  return (
    <Shell navKey="/warehouse-stock">
      <PageHead crumbs={[[t("home"), "/home"], [t("warehouses")], [t("stockOnHand")]]} title={t("stockOnHand") + (whName ? ` · ${whName}` : "")}
        sub={data ? <>{t("stockValue")}: <b className="num" data-testid="s-onhand-value">{amt(data.value)}</b></> : undefined} />
      <ListView rows={rows?.map((r) => ({ ...r, id: `${r.warehouse.id}|${r.item.id}` })) ?? null} error={failed} columns={columns} file="stock" testId="stock-table" onRefresh={() => void load()}
        search={[{ k: "cat", label: t("categoryName"), type: "select", options: (opts?.categories ?? []).map((c) => [c.id, L(c)]) }, { k: "item", label: t("itemName"), type: "text" }]}
        filters={filters} onFilters={setFilters} above={above} tools={<button className="btn btn-sm" onClick={() => window.print()}><Icon name="printer" /><span className="hide-sm">{t("print")}</span></button>}
        quick={(r) => `${r.item.nameEn} ${r.item.nameAr} ${r.item.code} ${L(r.warehouse)}`}
        empty={{ icon: "box", title: t("noStockRows"), body: t("noStockBody") }}
        actions={(r) => [
          { label: t("view"), icon: "eye", run: () => nav(`/items/show/${r.item.id}`) },
          { label: t("newCountHere"), icon: "check", run: () => nav(editorPath("inventory", r.warehouse.id)), ...(s.can(STOCK_KEYS.inventory.add) ? {} : lockTag(t)) }
        ]} />
    </Shell>
  );
}
